import React, { useState, useEffect, useRef } from 'react';

// width/height accept numbers (px) or CSS strings ("100%").
// containerStyle overrides the default absolute desktop positioning,
// e.g. { position: 'relative', left: 0 } for the mobile layout.
export const Slideshow = ({
    images,
    width = 600,
    height = 600,
    containerStyle = {}
  }) => {
    const [slideIndex, setSlideIndex] = useState(1);
    const touchStartX = useRef(null);

    const plusSlides = (n) => {
      let newIndex = slideIndex + n;
      if (newIndex > images.length) newIndex = 1;
      if (newIndex < 1) newIndex = images.length;
      setSlideIndex(newIndex);
    };

    const currentSlide = (n) => {
      setSlideIndex(n);
    };

    useEffect(() => {
      // Keyed on slideIndex so manual navigation resets the 4s auto-advance
      const timer = setTimeout(() => {
        setSlideIndex((prev) => (prev >= images.length ? 1 : prev + 1));
      }, 4000);

      return () => clearTimeout(timer);
    }, [slideIndex, images.length]);

    const handleTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
      if (touchStartX.current === null) return;
      const deltaX = e.changedTouches[0].clientX - touchStartX.current;
      if (Math.abs(deltaX) > 50) plusSlides(deltaX < 0 ? 1 : -1);
      touchStartX.current = null;
    };

    const toCss = (v) => (typeof v === 'number' ? `${v}px` : v);

    const baseContainerStyle = {
      position: "absolute",
      left: 400,
      width: toCss(width),
      height: toCss(height),
      overflow: "hidden",
      borderRadius: "8px",
      backgroundColor: "#000",
      ...containerStyle
    };

    const slideStyle = {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      transition: "opacity 0.6s ease-in-out"
    };

    const blurredBackgroundStyle = {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundSize: "cover",
      backgroundPosition: "center",
      filter: "blur(8px)",
      opacity: 0.5,
      transform: "scale(1.01)",
      zIndex: 0
    };

    const imageStyle = {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      maxWidth: "100%",
      maxHeight: "100%",
      width: "auto",
      height: "auto",
      objectFit: "contain",
      zIndex: 1
    };

    const dotsContainerStyle = {
      textAlign: "center",
      position: "absolute",
      bottom: "15px",
      width: "100%",
      zIndex: 2
    };

    const dotStyle = {
      cursor: "pointer",
      height: "15px",
      width: "15px",
      margin: "0 2px",
      backgroundColor: "rgba(255, 255, 255, 0.5)",
      borderRadius: "50%",
      display: "inline-block",
      transition: "background-color 0.6s ease"
    };

    const activeDotStyle = {
      ...dotStyle,
      backgroundColor: "#ffffff"
    };

    const prevNextButtonStyle = {
      cursor: "pointer",
      position: "absolute",
      top: "50%",
      width: "auto",
      padding: "16px",
      marginTop: "-22px",
      color: "white",
      fontWeight: "bold",
      fontSize: "18px",
      transition: "0.6s ease",
      borderRadius: "0 3px 3px 0",
      userSelect: "none",
      backgroundColor: "rgba(0,0,0,0.3)",
      zIndex: 2,
      border: "none",
      outline: "none",
      "&:hover": {
        backgroundColor: "rgba(0,0,0,0.8)"
      }
    };

    return (
      <div
        style={baseContainerStyle}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {images.map((img, index) => (
          <div
            key={index}
            style={{
              ...slideStyle,
              opacity: index + 1 === slideIndex ? 1 : 0
            }}
          >
            <div
              style={{
                ...blurredBackgroundStyle,
                backgroundImage: `url(${img})`
              }}
            />
            <img
              src={img}
              alt={`Slide ${index + 1}`}
              style={imageStyle}
            />
          </div>
        ))}

        <div style={dotsContainerStyle}>
          {images.map((_, index) => (
            <span
              key={index}
              style={index + 1 === slideIndex ? activeDotStyle : dotStyle}
              onClick={() => currentSlide(index + 1)}
            />
          ))}
        </div>

        <button
          style={{ ...prevNextButtonStyle, left: "0" }}
          onClick={() => plusSlides(-1)}
          aria-label="Previous slide"
        >❮</button>
        <button
          style={{ ...prevNextButtonStyle, right: "0", borderRadius: "3px 0 0 3px" }}
          onClick={() => plusSlides(1)}
          aria-label="Next slide"
        >❯</button>
      </div>
    );
  };
